/**
 * ONE LIST OF PER-PLAYER VIEWS, IN TWO VOICES.
 *
 * Two pages render the same rows about a player: the staff inspector's `/player/:uuid`, which is
 * somebody looking at somebody else, and `/me`, which is a person looking at themselves. The views
 * are identical in shape - `web.player_homes` and `web_self.player_homes` are the same columns in
 * the same order, which is asserted catalogue-to-catalogue by dev-stack.sh - so the only thing that
 * genuinely differs is the pronoun.
 *
 * THAT IS WHY THIS IS A LIST AND NOT TWO. A second array would drift: the day a migration adds
 * `web.player_warp_uses` it would appear on one page and not the other, and nobody would notice
 * because both pages would still look finished. Here a view is one entry with both wordings, and
 * both pages fail to compile until the entry exists (each holds a `Record` keyed by its own view
 * names - see the totality note on each page).
 *
 * NO COMPONENTS LIVE HERE, AND THE TWO PAGES NO LONGER SHARE ONE. `/player/:uuid` still renders
 * every view with the generic `components/ViewCard.tsx`, which is right for an admin comparing raw
 * columns; `/me` renders a hand-written card per view (`../me/sections.tsx`) because a player wants
 * a sentence rather than a column. This file is what they still share: the copy, in two voices, and
 * the order the views are read in. Adding a view here is what makes both pages fail to compile.
 *
 * THE SELF VOICE NAMES NO COMMANDS ANY MORE. It used to end "Change it in game with /tpablock",
 * which is not a command this mod registers (`/tpatoggle` is - see `TpaCommand`), and a caption is
 * the wrong place to find that out. The in-game commands now live in the cards that show them,
 * beside the row they act on, where they can be checked against the dispatcher.
 */
import { isPlayerViewName, type AuditViewName, type PlayerViewName, type WebViewName } from '@dimensionscore/shared';

/** The wording one page uses for one view. */
export interface ViewVoice {
  /** One line explaining what the view is for, since column names are terse. */
  readonly caption: string;
  /** Wording for "there is nothing here", which is usually a fact and not a failure. */
  readonly emptyText: string;
}

/** The views the per-player inspector shows as cards: every served view but the header and the audit tabs. */
export type InspectorViewName = Exclude<WebViewName, 'player_directory' | AuditViewName>;

export interface PlayerViewSpec {
  /**
   * Any per-player view EXCEPT the directory, which is the page header on both pages rather than a
   * card, and the server-wide audit surfaces (`AUDIT_VIEW_NAMES`), which the Audit page renders as
   * tabs. Excluded in the type rather than by convention so that both pages can index a `Record`
   * keyed by their own view names with no cast and no runtime fallback.
   */
  readonly view: InspectorViewName;
  readonly title: string;
  /** How the staff inspector says it: third person, about somebody else. */
  readonly staff: ViewVoice;
  /**
   * How the self-service page says it: second person, about the reader.
   *
   * Absent for the two views a player cannot reach at all (`outbox_status`, `audit_log`): the
   * `web_player` Postgres role holds no usage on schema `web`, so there is no wording to write.
   * The absence is what `SELF_VIEW_SPECS` filters on, so the two facts are one fact.
   */
  readonly self?: ViewVoice;
}

/**
 * Every per-player view the mod's migrations expose, in the order they are read.
 *
 * `player_directory` is not here: on both pages it is the header - who this is, when they were last
 * seen - rather than a card, and it is fetched separately.
 */
export const PLAYER_VIEW_SPECS: readonly PlayerViewSpec[] = [
  {
    view: 'player_homes',
    title: 'Homes',
    staff: {
      caption: 'Saved teleport destinations owned by this player.',
      emptyText: 'This player has set no homes.',
    },
    self: {
      caption: 'The places you can teleport straight back to.',
      emptyText: 'You have not saved a home yet.',
    },
  },
  {
    view: 'player_pending_rewards',
    title: 'Reward mailbox',
    staff: {
      caption: 'Queued and not yet claimed. `payload` is the reward union; `display_item` is only the icon.',
      emptyText: 'Nothing queued.',
    },
    self: {
      caption: 'Things the server is holding for you until you next collect them in game.',
      emptyText: 'Nothing is waiting for you right now.',
    },
  },
  {
    view: 'player_redemptions',
    title: 'Mystery gifts redeemed',
    staff: {
      caption: 'Gift codes this player has already claimed.',
      emptyText: 'No gift codes redeemed.',
    },
    self: {
      caption: 'Gift codes you have already claimed. Each code works once.',
      emptyText: 'You have not redeemed a gift code yet.',
    },
  },
  {
    view: 'player_secret_bases',
    title: 'Secret bases',
    staff: {
      caption: 'Bases this player owns, with their default and logout flags folded in.',
      emptyText: 'This player owns no bases.',
    },
    self: {
      caption: 'Your own private worlds, and which one you land in by default.',
      emptyText: 'You do not have a base yet.',
    },
  },
  {
    view: 'player_base_trusts',
    title: 'Trusted on',
    staff: {
      caption: 'Bases owned by OTHER players that this player has been trusted on.',
      emptyText: 'Not trusted on anyone else s base.',
    },
    self: {
      caption: 'Other players\u2019 bases you have been trusted with. They are named because they chose to trust you.',
      emptyText: 'Nobody has trusted you with their base yet.',
    },
  },
  {
    view: 'player_logout_placement',
    title: 'Logout placement',
    staff: {
      caption:
        'Where this player logged out, including the two states the bases view cannot show: inside someone else s base, and eject-pending with no base at all.',
      emptyText: 'This player did not log out inside a base.',
    },
    self: {
      caption: 'Where you will wake up, if you logged out inside a base.',
      emptyText: 'You were not inside a base when you last logged out.',
    },
  },
  {
    view: 'player_teleport_prefs',
    title: 'Teleport preferences',
    staff: {
      caption: 'Whether this player is accepting teleport requests.',
      emptyText: 'Defaults (no preference row).',
    },
    self: {
      caption: 'Whether other players may ask to teleport to you.',
      emptyText: 'You are on the default, so nothing is stored for you here.',
    },
  },
  /*
   * The two write-side views, which are per-player exactly like the seven above and so cost one
   * entry each. `outbox_status` is what the web ASKED FOR and `audit_log` is what the mod DID; they
   * are separate surfaces rather than one because a failed intent is never audited and an audit row
   * outlives the intent that caused it (see migration 1.8).
   *
   * NO `self` VOICE, and that is the enforcement's own shape showing through: they are server-wide
   * staff surfaces carrying every actor's Discord snowflake, and `web_player` cannot name them.
   */
  {
    view: 'outbox_status',
    title: 'Grants in flight',
    staff: {
      caption: 'Intents filed by the web for this player. `pending`/`claimed` are in flight; `done` and `failed` are the mod’s answer.',
      emptyText: 'No web-issued grant has ever been filed for this player.',
    },
  },
  {
    view: 'audit_log',
    title: 'Applied grants',
    staff: {
      caption: 'What the mod actually did, written after the fact. A refused intent leaves no row here.',
      emptyText: 'Nothing has been applied to this player through the web.',
    },
  },
];

/** One spec with the self voice resolved, for the page that has one. */
export interface SelfViewSpec {
  readonly view: Exclude<PlayerViewName, 'player_directory'>;
  readonly title: string;
  readonly caption: string;
  readonly emptyText: string;
}

/**
 * The subset a `player`-tier session can actually read, in the same order.
 *
 * DERIVED, NOT RE-LISTED, AND FILTERED TWICE ON PURPOSE. A view is here only if it has a self
 * voice (somebody wrote the second-person wording, i.e. decided this is a player's own business)
 * AND `isPlayerViewName` agrees (the shared allow-list the SIDECAR checks the same request
 * against). Either alone would be enough today; both together mean the SPA cannot ask for a
 * surface the sidecar will refuse, and cannot silently drop one the sidecar would allow, without
 * the two facts being visibly out of step here.
 */
export const SELF_VIEW_SPECS: readonly SelfViewSpec[] = PLAYER_VIEW_SPECS.flatMap((spec) => {
  if (spec.self === undefined || !isPlayerViewName(spec.view)) return [];
  return [{ view: spec.view, title: spec.title, caption: spec.self.caption, emptyText: spec.self.emptyText }];
});
